import { IController } from 'angular';
import * as _ from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';

import { GraduationPlanTransfer } from '../../../../../../ng2/shared/constants/graduation-plan-transfer.constant';
import { NextRegentsAdminDate } from '../../../../../../ng2/shared/constants/next-regents-admin-date.constant';
import { IRegentsCategory } from '../../../../../../ng2/shared/constants/regents.constant';
import { IWizardFilterActionsOptions } from '../../../components/wizard-filter-actions/wizard-filter-actions.typings';
import { GraduationPlan } from './../../../../../../ng2/shared/constants/graduation-plan.constant';
import { RegentsCategoryByCategory5 as CAT_5 } from './../../../../../../ng2/shared/constants/regents-category-by-category-5.constant';
import { RegentsCategoryByCategory9 as CAT_9 } from './../../../../../../ng2/shared/constants/regents-category-by-category-9.constant';
import { ISdcStore } from './../../../../../sdc/flux/sdc-store.interface';

type TValidToggles = 'CAT_5' | 'CAT_9' | 'MIN_REQ';
type TValidBaseObjectKeys = 'NOT_PLANNED' | 'PLANNED_THIS_ADMIN' | 'PLANNED_FUTURE' | 'FULFILLED' | 'NOT_YET_NEEDED';
type TValidCollegeReadinessKeys = 'Math' | 'ELA';

interface ITableHeader {
  humanName: string;
  neededOnTrackValue: string;
}

interface IBaseObject {
  [TValidBaseObjectKeys: string]: {
    [key: string]: string[];
  };
}

interface IHelper {
  [TValidBaseOBjectKey: string]: {
    language: string;
    style: string;
  };
}

interface ICollegeReadinessData {
  [TValidCollegeReadinessKeys: string]: { fulfilled: string[]; notReady: string[]; notScheduled: string[] };
}

interface IMisMatchData {
  mismatchedStudents: string[];
  totalExams: number;
}

export class RegentsPlanningTableCtrl implements IController {
  static inject: string[] = ['$scope', 'SDCStore', 'ImSchool'];

  public nextAdminDate;
  public filteredStudents; // sdc students
  public studentsByGradPlan; // sdc students
  public studentIdsByGradPlan: string[];
  public studentsWithUnadressedNeeds: string[];
  public portalMismatchData: IMisMatchData;
  public starsMismatchData: IMisMatchData;
  public studentCollegeReadinessData: ICollegeReadinessData;
  public tableHeaders: ITableHeader[];
  public tableToggleValue: TValidToggles = 'CAT_5';
  public activeCategories: IRegentsCategory[];
  public helper: IHelper;
  public table: IBaseObject;
  public tableOptions: IWizardFilterActionsOptions;
  public tableOptions_diploma: IWizardFilterActionsOptions;
  public tableOptions_grad: IWizardFilterActionsOptions;
  public readinessOptionsEla: IWizardFilterActionsOptions;
  public readinessOptionsMath: IWizardFilterActionsOptions;
  public portalToStarsOptions: IWizardFilterActionsOptions;
  public unaddressedOptions: IWizardFilterActionsOptions;

  constructor(private $scope, private SDCStore: ISdcStore, private ImSchool: ImSchool) {}

  $onInit(): void {
    this.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        this.setUi();
        this.setListeners();
      }
    });
  }

  setUi(): void {
    this.filteredStudents = this.SDCStore.getFilteredFlattenedStudents();
    this.nextAdminDate = NextRegentsAdminDate;
    this.helper = {
      NOT_PLANNED: { language: 'Needed / Not Planned', style: 'red' },
      PLANNED_THIS_ADMIN: { language: `Needed / Planned ${NextRegentsAdminDate}`, style: 'green' },
      PLANNED_FUTURE: { language: 'Needed / Planned Future Admin', style: 'yellow' },
      FULFILLED: { language: 'Fulfilled', style: 'green' },
      NOT_YET_NEEDED: { language: 'Not Yet Needed', style: 'grey' },
    };

    this.tableOptions_diploma = {
      columnKeys: [
        'regentsPassedForDiploma',
        'regentsNotYetNeededForDiploma',
        'regentsNeededForOnTrackDiplomaNotSched',
        'regentsNeededForOnTrackDiplomaSchedNextAdmin',
        'regentsNeededForOnTrackDiplomaSchedFutureAdmin',
      ],
    };

    this.tableOptions_grad = {
      columnKeys: [
        'regentsPassedForGrad',
        'regentsNotYetNeededForGrad',
        'regentsNeededForOnTrackGradNotSched',
        'regentsNeededForOnTrackGradSchedNextAdmin',
        'regentsNeededForOnTrackGradSchedFutureAdmin',
      ],
    };

    this.readinessOptionsMath = {
      columnKeys: ['maxScoreMath', 'collegeReadyOnMathRegentsOrSatMath', 'regentsSchedForNextAdmin'],
    };

    this.readinessOptionsEla = {
      columnKeys: ['maxScoreEnglishExams', 'collegeReadyOnElaRegentsOrSatCr', 'regentsSchedForNextAdmin'],
    };

    this.portalToStarsOptions = {
      columnKeys: ['examsSchedInPortalNotPlannedInStars', 'examsSchedInStarsNotPlannedInPortal'],
    };

    this.unaddressedOptions = {
      columnKeys: ['regentsNeededForOnTrackDiplomaNotSched'],
    };

    this.buildTable();
  }

  // wraps all table functionality for toggle/store updates
  buildTable(): void {
    this.activeCategories =
      this.tableToggleValue === 'CAT_5' || this.tableToggleValue === 'MIN_REQ' ? CAT_5.required : CAT_9.required;
    this.studentsByGradPlan = this.filterStudentsByGradPlan();
    this.studentIdsByGradPlan = _.map(this.studentsByGradPlan, ({ studentId }) => studentId);
    this.tableHeaders = this.getTableHeaders();
    this.tableOptions = this.tableToggleValue === 'MIN_REQ' ? this.tableOptions_grad : this.tableOptions_diploma;
    this.table = this.getTableRows();
    this.buildPublicCalculations();
  }

  // wraps all calc functionality for toggle/store updates
  private buildPublicCalculations(): void {
    this.studentsWithUnadressedNeeds = this.getStudentsWithUnadressedNeeds();
    this.portalMismatchData = this.getMismatchedPortalStudentsData();
    this.starsMismatchData = this.getMismatchedStarsStudentsData();
    this.studentCollegeReadinessData = this.getStudentCollegeReadinessData();
  }

  private getTableHeaders(): ITableHeader[] {
    return _.map(this.activeCategories, ({ humanName, neededOnTrackValue }) => ({ humanName, neededOnTrackValue }));
  }

  private getBaseObject(): IBaseObject {
    // used to populate tableRows
    const base = { NOT_PLANNED: {}, PLANNED_FUTURE: {}, PLANNED_THIS_ADMIN: {}, FULFILLED: {}, NOT_YET_NEEDED: {} };
    return _.forOwn(base, (tableColumns, tableRow) => {
      _.each(this.tableHeaders, ({ neededOnTrackValue }) => {
        tableColumns[neededOnTrackValue] = [];
      });
    });
  }

  // populates baseObject with studentIds
  private getTableRows(): IBaseObject {
    const columns = this.getTableHeaders();
    const baseObj = this.getBaseObject();
    const tableRows = _.reduce(
      this.studentsByGradPlan,
      (tableRow, student: any) => {
        const toggleIsMinReqForGrad = this.tableToggleValue === 'MIN_REQ';
        const NOT_PLANNED = toggleIsMinReqForGrad
          ? student.regentsNeededForOnTrackGradNotSched
          : student.regentsNeededForOnTrackDiplomaNotSched;
        const PLANNED_THIS_ADMIN = toggleIsMinReqForGrad
          ? student.regentsNeededForOnTrackGradSchedNextAdmin
          : student.regentsNeededForOnTrackDiplomaSchedNextAdmin;
        const PLANNED_FUTURE = toggleIsMinReqForGrad
          ? student.regentsNeededForOnTrackGradSchedFutureAdmin
          : student.regentsNeededForOnTrackDiplomaSchedFutureAdmin;
        const FULFILLED = toggleIsMinReqForGrad ? student.regentsPassedForGrad : student.regentsPassedForDiploma;
        const NOT_YET_NEEDED = toggleIsMinReqForGrad
          ? student.regentsNotYetNeededForGrad
          : student.regentsNotYetNeededForDiploma;
        const studentData = {
          NOT_PLANNED,
          PLANNED_THIS_ADMIN,
          PLANNED_FUTURE,
          FULFILLED,
          NOT_YET_NEEDED,
        };
        _.each(columns, (examCat: any) => {
          const neededOnTrackValue = examCat.neededOnTrackValue;
          // We need to loop through these statuses in an ordered way because we only
          // want each student to show up in one bucket for each exam
          // Meaning, is a student is FULFILLED for ELA, we want to push them to the fulfilled bucket
          // and not any other bucket, even if they are also planned for the exam in the next or future admin
          const orderedStatuses = [
            'FULFILLED',
            'NOT_YET_NEEDED',
            'PLANNED_THIS_ADMIN',
            'PLANNED_FUTURE',
            'NOT_PLANNED',
          ];
          _.some(orderedStatuses, status => {
            const examCatHasThisStatus = _.includes(studentData[status], neededOnTrackValue);
            if (examCatHasThisStatus && tableRow[status][neededOnTrackValue]) {
              tableRow[status][neededOnTrackValue].push(student.studentId);
              return true; // break out of the loop
            }
          });
        });
        return tableRow;
      },
      baseObj,
    );
    return tableRows;
  }

  // returns sdc flattenedStudents
  private filterStudentsByGradPlan() {
    const tableToggle = this.tableToggleValue;
    // TODO: REMOVE THIS + LOGIC ONCE REGENTS WIZ IS BUILT FOR TRANSFER SCHOOLS
    const school = this.SDCStore.getSchool();
    const schoolIsTransfer = this.ImSchool.isTransferSchool(school);
    // maps flattendStudent `gradPlan` to toggleValue and filters down students
    const toggleMappingObj = {
      CAT_5: ['Local', 'Regents'],
      CAT_9: ['Advanced Regents'],
    };
    if (tableToggle === 'MIN_REQ') return this.filteredStudents;
    const studentsForToggle = _.filter(this.filteredStudents, (student: any) => {
      // sdc student
      const { gradPlan: studentGradPlan } = student;
      const filter = toggleMappingObj[tableToggle];
      let diplomaType;
      if (schoolIsTransfer) {
        diplomaType = _.find(GraduationPlanTransfer, { humanName: studentGradPlan }).diploma;
      } else diplomaType = _.find(GraduationPlan, { humanName: studentGradPlan }).diploma;
      if (_.includes(filter, diplomaType)) return student;
    });
    return studentsForToggle;
  }

  private getStudentsWithUnadressedNeeds() {
    return _.reduce(
      this.studentsByGradPlan,
      (studentIdsArray, { studentId, regentsNeededForOnTrackDiplomaNotSched: NOT_PLANNED }) => {
        if (NOT_PLANNED.length > 0) studentIdsArray.push(studentId);
        return studentIdsArray;
      },
      [],
    );
  }

  private getMismatchedPortalStudentsData(): IMisMatchData {
    return _.reduce(
      this.filteredStudents,
      (result, { studentId, examsSchedInStarsNotPlannedInPortal: mismatchedExams }) => {
        if (mismatchedExams.length) {
          result.mismatchedStudents.push(studentId);
          result.totalExams += mismatchedExams.length;
        }
        return result;
      },
      { mismatchedStudents: [], totalExams: 0 },
    );
  }

  private getMismatchedStarsStudentsData(): IMisMatchData {
    return _.reduce(
      this.filteredStudents,
      (result, { studentId, examsSchedInPortalNotPlannedInStars: mismatchedExams }) => {
        if (mismatchedExams.length) {
          result.mismatchedStudents.push(studentId);
          result.totalExams += mismatchedExams.length;
        }
        return result;
      },
      { mismatchedStudents: [], totalExams: 0 },
    );
  }

  private getStudentCollegeReadinessData(): ICollegeReadinessData {
    const baseObj = {
      ELA: { fulfilled: [], notReady: [], notScheduled: [] },
      Math: { fulfilled: [], notReady: [], notScheduled: [] },
    };
    return _.reduce(
      this.filteredStudents,
      (
        result,
        {
          studentId,
          maxScoreEnglishExams,
          collegeReadyOnElaRegentsOrSatCr,
          maxScoreMath,
          collegeReadyOnMathRegentsOrSatMath,
          regentsSchedForNextAdmin,
        },
      ) => {
        if (+maxScoreEnglishExams >= 65) {
          result.ELA.fulfilled.push(studentId);
          if (collegeReadyOnElaRegentsOrSatCr === 'No') {
            result.ELA.notReady.push(studentId);
            if (!_.includes(regentsSchedForNextAdmin, 'CC ELA')) result.ELA.notScheduled.push(studentId);
          }
        }
        if (+maxScoreMath >= 65) {
          result.Math.fulfilled.push(studentId);
          if (collegeReadyOnMathRegentsOrSatMath === 'No') {
            result.Math.notReady.push(studentId);
            if (!_.includes(regentsSchedForNextAdmin, 'CC Alg')) result.Math.notScheduled.push(studentId);
          }
        }
        return result;
      },
      baseObj,
    );
  }

  private setListeners(): void {
    this.$scope.$listenTo(this.SDCStore, ['filteredFlattenedStudents'], this.setUi.bind(this));
    this.$scope.$listenTo(this.SDCStore, ['school'], this.setUi.bind(this));
  }
}
